import React, { useState, useEffect, useRef, useId } from 'react';
import { useReducedMotion } from 'framer-motion';
import styled from 'styled-components';
import laurelLeading from '../assets/images/laurel-leading.png';
import laurelTrailing from '../assets/images/laurel-trailing.png';
import SterlingCoverflow from '../components/showcase/SterlingCoverflow';

// ----- Brand tokens (matched 1:1 to Lovable wireframe) -----
const cream = '#F8F4EE';
const ink = '#1C1C1C';
const muted = '#6B6259';
const terracotta = '#C75D3D';
const terracottaDark = '#A64F33';
const border = 'rgba(28, 28, 28, 0.10)';

const SerifFamily = `'Crimson Pro', 'Cormorant Garamond', Georgia, serif`;

// ===== Shell =====
const PageContainer = styled.div`
  padding-top: 71px;
  background: ${cream};
  color: ${ink};
  font-family: var(--font-body);
  min-height: 100vh;
`;

const Container = styled.div`
  width: 90%;
  max-width: 1080px;
  margin: 0 auto;
`;

const SectionWrap = styled.section`
  padding: 96px 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

// ===== Typography =====
const Eyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${terracotta};
  font-weight: 600;
  margin-bottom: 1.25rem;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${muted};
  font-weight: 500;
  margin-bottom: 0.75rem;
`;

const HeroHeadline = styled.h1`
  font-family: ${SerifFamily};
  font-weight: 600;
  color: ${ink};
  line-height: 1;
  letter-spacing: -0.025em;
  font-size: clamp(3rem, 6vw, 5rem);
  margin: 0 0 1.5rem;
`;

const SerifTitle = styled.h2`
  font-family: ${SerifFamily};
  font-weight: 600;
  color: ${ink};
  line-height: 1.05;
  letter-spacing: -0.02em;
  font-size: clamp(2.25rem, 4vw, 3.25rem);
  margin: 0 0 0.75rem;
  text-align: center;
`;

const Body = styled.p`
  font-size: 1.05rem;
  line-height: 1.65;
  color: ${muted};
  max-width: 640px;
  margin: 0 auto;
`;

// ===== Hero =====
const HeroSection = styled.section`
  padding: 96px 24px 32px;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 64px 16px 32px;
  }
`;

const HeroInner = styled.div`
  max-width: 880px;
  margin: 0 auto;
`;

const HeroSub = styled.p`
  font-size: 1.2rem;
  line-height: 1.55;
  color: ${muted};
  max-width: 600px;
  margin: 0 auto 2rem;
`;

const CTARow = styled.div`
  display: flex;
  gap: 1.25rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  background: ${terracotta};
  color: #fff !important;
  padding: 14px 22px;
  border-radius: var(--radius-pill);
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  text-decoration: none !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    color: #fff !important;
    background: ${terracottaDark};
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(199, 93, 61, 0.28);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// The same pill again, but inert: no href, no hover lift, muted ink and a
// dashed edge so it reads as a state rather than a control. A span, not an
// anchor or a button, so assistive tech does not announce something activatable.
const StorePendingCTA = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  background: transparent;
  color: ${muted};
  padding: 13px 21px;
  border-radius: var(--radius-pill);
  font-weight: 600;
  font-size: 0.95rem;
  border: 1px dashed rgba(28, 28, 28, 0.20);
  cursor: default;

  svg { width: 16px; height: 16px; opacity: 0.75; }
`;

// The App Store's twin for Google Play: identical pill and metrics, outlined
// rather than filled, so the row reads as two peers without a second solid
// terracotta block fighting the first.
const StoreOutlineCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  background: transparent;
  color: ${ink} !important;
  padding: 13px 21px;
  border-radius: var(--radius-pill);
  font-weight: 600;
  font-size: 0.95rem;
  border: 1px solid rgba(28, 28, 28, 0.22);
  text-decoration: none !important;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    color: ${ink} !important;
    border-color: ${terracotta};
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(28, 28, 28, 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SecondaryCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${ink};
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none !important;

  &:hover {
    color: ${terracotta};
  }
`;

// ===== Filter chips =====
const ChipStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 4rem auto 0;
  justify-content: center;
  max-width: 720px;

  @media (max-width: 768px) {
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    padding: 0 1rem;
    justify-content: flex-start;
  }
`;

const Chip = styled.div`
  padding: 7px 14px;
  border-radius: var(--radius-pill);
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${border};
  color: ${ink};
  background: transparent;
  white-space: nowrap;

  &.active {
    background: ${terracotta};
    color: #fff;
    border-color: ${terracotta};
  }
`;

// ===== Apple logo =====
const AppleLogoSVG = () => (
  <svg viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

// Monochrome on purpose. Google's own mark is four-colour, which would put a
// second and third accent on a page that allows exactly one.
const GooglePlayLogoSVG = () => (
  <svg viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

// ===== Laurel - Apple's `laurel.leading` / `laurel.trailing` SF Symbols
//        rendered exactly as the iOS app shows them.  PNGs were extracted
//        from a Sterling Standard restaurant header in the simulator and
//        upscaled 4x for retina; using the actual symbol guarantees the
//        marketing site and the app are visually identical. =====
const LaurelImg = styled.img`
  display: block;
  flex-shrink: 0;
  user-select: none;
`;

const Laurel = ({ flip = false, size = 64 }) => (
  <LaurelImg
    src={flip ? laurelTrailing : laurelLeading}
    alt=""
    aria-hidden="true"
    width={flip ? 240 : 244}
    height={408}
    loading="lazy"
    decoding="async"
    style={{ height: size, width: 'auto' }}
  />
);

// ===== Two-col problem framing =====
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ProblemTitle = styled.h2`
  font-family: ${SerifFamily};
  font-weight: 600;
  font-size: clamp(2rem, 3.5vw, 2.75rem);
  line-height: 1.1;
  color: ${ink};
  margin: 0 0 1rem;
  letter-spacing: -0.02em;
`;

// ----- Compare card: stars + felt-quote on the left, real inspection
//        record on the right.  Mirrors the Lovable wireframe pattern:
//        people's feelings vs. what an inspector actually wrote down. -----
const tierForest = '#3F5440';

const CompareCard = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1px 1.5fr;
  align-items: stretch;
  background: #fff;
  border: 1px solid ${border};
  border-radius: 16px;
  padding: 2rem;
  gap: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const FeltColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.85rem;
`;

const FeltStars = styled.div`
  font-size: 2.2rem;
  color: rgba(28, 28, 28, 0.16);
  letter-spacing: 0.5rem;
  font-family: serif;
  line-height: 1;
`;

const FeltCaption = styled.div`
  font-size: 10px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${muted};
  font-weight: 600;
`;

const FeltQuote = styled.div`
  font-family: ${SerifFamily};
  font-style: italic;
  font-size: 1rem;
  color: ${muted};
  line-height: 1.55;
  max-width: 240px;
`;

const VRule = styled.div`
  width: 1px;
  background: ${terracotta};
  opacity: 0.32;

  @media (max-width: 640px) {
    width: 100%;
    height: 1px;
  }
`;

const RecordColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const RecordHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid ${border};
  margin-bottom: 0.5rem;
`;

const RecordTitle = styled.div`
  font-size: 10px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${terracotta};
  font-weight: 700;
`;

const RecordDate = styled.div`
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${muted};
  font-weight: 500;
  white-space: nowrap;
  margin-left: 0.75rem;
`;

const RecordItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 0.55rem 0;
  font-size: 0.92rem;
  color: ${ink};
`;

const RecordLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-width: 0;
`;

const RecordPass = styled.div`
  color: ${tierForest};
  font-weight: 600;
  font-size: 0.78rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
`;

const ScoreFooter = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid ${border};
`;

const ScoreLabel = styled.div`
  font-size: 11px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${muted};
  font-weight: 600;
`;

const ScoreValue = styled.div`
  font-family: ${SerifFamily};
  font-weight: 600;
  color: ${terracotta};
  font-size: 1.7rem;
  letter-spacing: -0.02em;
  line-height: 1;

  span {
    color: ${muted};
    font-size: 0.95rem;
    margin-left: 0.25rem;
    font-family: var(--font-body);
  }
`;

const RecordCheck = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="7" fill={tierForest} opacity="0.14" />
    <path
      d="M4 7L6.2 9L10 5.2"
      stroke={tierForest}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ===== How it works =====
const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  background: #fff;
  border: 1px solid ${border};
  border-radius: 16px;
  padding: 2rem 1.75rem;
`;

const StepNum = styled.div`
  font-family: ${SerifFamily};
  font-size: 2.4rem;
  font-weight: 600;
  color: ${terracotta};
  line-height: 1;
  margin-bottom: 1rem;
`;

const StepTitle = styled.h3`
  font-family: ${SerifFamily};
  font-size: 1.4rem;
  color: ${ink};
  margin: 0 0 0.6rem;
  font-weight: 600;
`;

const StepBody = styled.p`
  color: ${muted};
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

const HRule = styled.div`
  width: 80px;
  height: 1px;
  background: ${terracotta};
  margin: 4rem auto 0;
  opacity: 0.5;
`;

// ===== Sterling Standard =====
const StandardWrap = styled.div`
  text-align: center;
  position: relative;
  padding: 1rem 0;
`;

const StandardMarquee = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  margin: 1.5rem auto 1.5rem;
  flex-wrap: nowrap;
  color: ${terracotta};

  @media (max-width: 900px) {
    gap: 0.75rem;
    flex-wrap: wrap;
  }
`;

const StandardName = styled.div`
  font-family: ${SerifFamily};
  font-size: clamp(2.25rem, 5.2vw, 3.75rem);
  font-weight: 600;
  color: ${ink};
  line-height: 1;
  letter-spacing: -0.02em;
  white-space: nowrap;
  animation: nameIn 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes nameIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 900px) {
    white-space: normal;
    font-size: 1.85rem;
  }
`;

// Wrapper exists so hover/focus can pause the rotation without the whole
// section counting as "hovered".
const NameRotator = styled.div``;

// The register's own voice: the small uppercase caption used by StatLabel and
// FeltCaption, made interactive.
const RotationToggle = styled.button`
  margin-top: 0.25rem;
  background: transparent;
  border: none;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  font-size: 10px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  font-weight: 600;
  color: ${muted};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    background: transparent;
    color: ${terracotta};
  }
`;

const StandardItalic = styled.div`
  font-family: ${SerifFamily};
  font-style: italic;
  font-size: 1.4rem;
  color: ${muted};
  margin-bottom: 1.5rem;
`;

const StatStrip = styled.div`
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  border-top: 1px solid ${border};
  border-bottom: 1px solid ${border};
  padding: 2.5rem 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

const StatCell = styled.div`
  text-align: center;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 10%;
    height: 80%;
    width: 1px;
    background: ${border};

    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const StatNumber = styled.div`
  font-family: ${SerifFamily};
  font-size: 2.5rem;
  font-weight: 600;
  color: ${terracotta};
  line-height: 1;
  letter-spacing: -0.02em;
`;

const StatLabel = styled.div`
  font-size: 10px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${muted};
  margin-top: 0.65rem;
`;

// ===== Cities =====
const CitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.85rem;
  margin-top: 2.75rem;

  @media (max-width: 980px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 680px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CityCard = styled.div`
  background: #fff;
  border: 1px solid ${border};
  border-radius: 12px;
  padding: 0.95rem 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: ${terracotta};
  }
`;

const CityName = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${ink};
`;

const CityCount = styled.div`
  font-size: 9.5px;
  color: ${muted};
  letter-spacing: 0.8px;
  text-transform: uppercase;
  margin-top: 0.2rem;
`;

const CityDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $hot }) => ($hot ? terracotta : 'rgba(28, 28, 28, 0.22)')};
  flex-shrink: 0;
  margin-left: 0.5rem;
`;

const CitiesFooter = styled.div`
  text-align: center;
  margin-top: 2rem;
  font-size: 11px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${muted};

  a {
    color: ${terracotta};
    font-weight: 600;
    margin-left: 0.5rem;
  }
`;

// ===== Pricing =====
const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  max-width: 920px;
  margin: 3rem auto 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PriceCard = styled.div`
  background: #fff;
  border: ${({ $featured }) => ($featured ? `1.5px solid ${terracotta}` : `1px solid ${border}`)};
  border-radius: 20px;
  padding: 2.25rem 2rem;
  position: relative;
  ${({ $featured }) => $featured && `box-shadow: 0 12px 36px rgba(199, 93, 61, 0.10);`}
`;

const PlanName = styled.div`
  font-size: 11px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${muted};
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const PlanList = styled.ul`
  list-style: none;
  margin: 1.75rem 0;
  padding: 0;
`;

const PlanItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.5rem 0;
  font-size: 0.95rem;
  color: ${ink};
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="9" fill={terracotta} />
    <path d="M5 9.5L8 12.5L13 7" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// An anchor, not a button: this navigates to the App Store, so it has to be a
// real link (middle-click, copy address, popup blockers).
const PrimaryButton = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  background: ${terracotta};
  color: #fff !important;
  padding: 14px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  text-decoration: none !important;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    color: #fff !important;
    background: ${terracottaDark};
  }
`;

// Inside a plan card the buttons are full-width blocks, so the pending state
// matches that shape rather than the hero's pill.
const PlanPendingNote = styled.div`
  display: block;
  width: 100%;
  text-align: center;
  margin-top: 0.6rem;
  padding: 14px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  color: ${muted};
  border: 1px dashed rgba(28, 28, 28, 0.20);
`;

const OutlineButton = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  background: transparent;
  color: ${ink} !important;
  padding: 14px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  border: 1px solid ${border};
  text-decoration: none !important;
  cursor: pointer;

  &:hover {
    color: ${ink} !important;
    border-color: ${terracotta};
  }
`;

// ===== FAQ =====
const FAQList = styled.div`
  max-width: 760px;
  margin: 3rem auto 0;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid ${border};
  padding: 1.4rem 0;
`;

const FAQQuestion = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${SerifFamily};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${ink};
  text-align: left;

  &:hover {
    background: transparent;
    color: ${terracotta};
  }

  span.caret {
    transition: transform 0.25s ease;
    transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'rotate(0deg)')};
    color: ${terracotta};
    font-size: 1.4rem;
    line-height: 1;
  }
`;

const FAQAnswer = styled.div`
  margin-top: 0.85rem;
  color: ${muted};
  font-size: 0.95rem;
  line-height: 1.65;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
`;

// ===== Final CTA =====
const FinalCTA = styled(SectionWrap)`
  border-top: 1px solid ${border};
  text-align: center;
  background: ${cream};
`;

const FinalCTASmallText = styled.div`
  font-size: 11px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${muted};
  margin-top: 1.25rem;
`;

// Legal-document footer row. Surfaces Privacy + Terms links from the
// Sterling marketing page so the iOS app's "Open on sterlingdine.com"
// CTAs land on a page where the documents are easy to find without
// needing to know the deep-link path.
const LegalLinksRow = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  font-size: 12px;
  letter-spacing: 0.6px;
  color: ${muted};

  a {
    color: ${terracotta};
    text-decoration: none;
    font-weight: 500;
    &:hover { text-decoration: underline; }
  }

  span {
    opacity: 0.5;
  }
`;

// ============= PAGE =============
// Real Sterling Standard restaurants pulled from the production DB -
// nine distinct cities, plus Detroit. Auto-rotates so the gold mark
// reads as a living register, not a one-off stunt.
const STERLING_NAMES = [
  'Sepia',
  'Parkside',
  'Finch',
  'Revel',
  'Mes Amis',
  'Pearl',
  'Dough',
  'Bloom',
  'Basan',
  'Drip Cafe',
];

const Sterling = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const faqNs = useId();
  const [nameIdx, setNameIdx] = useState(0);
  const [namePaused, setNamePaused] = useState(false);
  const [nameHeld, setNameHeld] = useState(false);
  const [nameInView, setNameInView] = useState(false);
  const nameRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const el = nameRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNameInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(([e]) => setNameInView(e.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // The largest text on the page swaps itself, so the rotation only runs when
  // it is on screen, un-paused, not under the pointer, and motion is welcome.
  useEffect(() => {
    if (reduceMotion || namePaused || nameHeld || !nameInView) return undefined;
    const id = setInterval(() => {
      setNameIdx((i) => (i + 1) % STERLING_NAMES.length);
    }, 3500);
    return () => clearInterval(id);
  }, [reduceMotion, namePaused, nameHeld, nameInView]);

  // Counts pulled from the live jurisdictions table on 2026-05-24
  // after migration 0076 deactivated four empty jurisdictions and
  // added the denormalized restaurant_count column. 18 active
  // jurisdictions; sorted by restaurant count (largest first) to
  // match the onboarding city picker order in the app.
  const cities = [
    { name: 'Florida (statewide)', count: '62,691', hot: true },
    { name: 'New York City', count: '25,757', hot: true },
    { name: 'Los Angeles County', count: '24,155', hot: true },
    { name: 'Seattle', count: '9,543', hot: true },
    { name: 'San Jose', count: '8,177', hot: true },
    { name: 'San Diego County', count: '7,988', hot: true },
    { name: 'Chicago', count: '7,501', hot: true },
    { name: 'NY State', count: '7,239', hot: true },
    { name: 'Pittsburgh', count: '5,853', hot: true },
    { name: 'Austin', count: '5,017', hot: true },
    { name: 'Bethesda (Montgomery MD)', count: '3,937', hot: false },
    { name: 'Boston', count: '3,276', hot: false },
    { name: 'Wilmington (Delaware)', count: '3,109', hot: false },
    { name: 'Detroit', count: '2,196', hot: false },
    { name: 'Marin County', count: '899', hot: false },
    { name: 'Columbus', count: '872', hot: false },
    { name: 'Boulder County', count: '298', hot: false },
    { name: 'Minneapolis', count: '109', hot: false },
  ];

  const faqs = [
    {
      q: 'Where does the data come from?',
      a: "Public health-inspection records published by each city or county's health department. Every record is traceable - tap any inspection in the app to see the original.",
    },
    {
      q: 'How often is data refreshed?',
      a: "Weekly. Sterling re-pulls every city's data on Tuesday mornings.",
    },
    {
      q: 'What does the Sterling Standard mean?',
      a: 'A strict tier earned by restaurants with score ≥ 95, three or more inspections in the last 24 months, and zero critical violations in the last 12 months. About 16% of restaurants qualify.',
    },
    {
      q: 'Is Sterling affiliated with any health department?',
      a: 'No. Sterling is independent and unaffiliated with any government agency. We just read public records.',
    },
    {
      q: "What's included?",
      a: 'Every rating is included, along with restaurant alerts, automatic travel-mode city switching, and health filters that hide restaurants with recent violations.',
    },
    {
      q: 'Can restaurant owners use Sterling?',
      a: 'Yes - owners of a currently Sterling Standard restaurant can get a printable Sterling Report, a framed-quality certificate to display their achievement.',
    },
  ];

  const APP_STORE_URL = 'https://apps.apple.com/us/app/sterling-dine-with-confidence/id6765960448';
  // The Play URL is correct and permanent -- it is built from the applicationId.
  // The listing went live on 2026-09-26. PLAY_LIVE = false would turn every
  // Google Play affordance on this page back into an inert "coming soon" pill.
  const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.sterlingdine.app';
  const PLAY_LIVE = true;

  return (
    <PageContainer>
      {/* Hero */}
      <HeroSection>
        <HeroInner>
          <Eyebrow>Sterling · iPhone &amp; Android</Eyebrow>
          <HeroHeadline>Dine with confidence.</HeroHeadline>
          <HeroSub>
            Sterling rates restaurants using real health-inspection records - not opinions, not stars. Every score
            traceable. Every record public.
          </HeroSub>
          <CTARow>
            <PrimaryCTA href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <AppleLogoSVG />
              Download on the App Store
            </PrimaryCTA>
            {PLAY_LIVE ? (
              <StoreOutlineCTA href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                <GooglePlayLogoSVG />
                Get it on Google Play
              </StoreOutlineCTA>
            ) : (
              <StorePendingCTA>
                <GooglePlayLogoSVG />
                Google Play - coming soon
              </StorePendingCTA>
            )}
            <SecondaryCTA href="#how-it-works">
              See how it works <span aria-hidden="true">→</span>
            </SecondaryCTA>
          </CTARow>
        </HeroInner>

        <ChipStrip>
          <Chip className="active">Featured</Chip>
          <Chip>Now on Android</Chip>
          <Chip>Health & Safety</Chip>
          <Chip>Map-first</Chip>
          <Chip>18 cities</Chip>
          <Chip>Updated weekly</Chip>
        </ChipStrip>
      </HeroSection>

      {/* Problem */}
      <SectionWrap>
        <Container>
          <TwoCol>
            <div>
              <SectionLabel>The problem</SectionLabel>
              <ProblemTitle>
                Reviews tell you what people <em>felt</em>. Inspections tell you what's <em>true</em>.
              </ProblemTitle>
              <Body style={{ margin: 0 }}>
                Star ratings reflect mood, presentation, and how the night went. They miss the things that actually
                matter for your health - refrigeration, hand-washing, pest control. Sterling reads what the city's
                inspectors actually wrote.
              </Body>
            </div>
            <CompareCard>
              <FeltColumn>
                <FeltStars aria-hidden="true">★★★★★</FeltStars>
                <FeltCaption>What people felt</FeltCaption>
                <FeltQuote>"Service was a little slow but the pasta was divine."</FeltQuote>
              </FeltColumn>
              <VRule />
              <RecordColumn>
                <RecordHeader>
                  <RecordTitle>Inspection record</RecordTitle>
                  <RecordDate>Mar 18, 2026</RecordDate>
                </RecordHeader>
                <RecordItem>
                  <RecordLabel>
                    <RecordCheck />
                    Cold-hold temperature
                  </RecordLabel>
                  <RecordPass>Pass</RecordPass>
                </RecordItem>
                <RecordItem>
                  <RecordLabel>
                    <RecordCheck />
                    Hand-washing facility
                  </RecordLabel>
                  <RecordPass>Pass</RecordPass>
                </RecordItem>
                <RecordItem>
                  <RecordLabel>
                    <RecordCheck />
                    Pest control
                  </RecordLabel>
                  <RecordPass>Pass</RecordPass>
                </RecordItem>
                <ScoreFooter>
                  <ScoreLabel>Score</ScoreLabel>
                  <ScoreValue>
                    98<span>/ 100</span>
                  </ScoreValue>
                </ScoreFooter>
              </RecordColumn>
            </CompareCard>
          </TwoCol>
        </Container>
      </SectionWrap>

      {/* How it works */}
      <SectionWrap id="how-it-works">
        <Container>
          <SerifTitle>How Sterling works.</SerifTitle>
          <StepGrid>
            <StepCard>
              <StepNum>01</StepNum>
              <StepTitle>Public records in</StepTitle>
              <StepBody>
                Sterling pulls every routine inspection your city has published, normalizes the data, and indexes it by
                restaurant.
              </StepBody>
            </StepCard>
            <StepCard>
              <StepNum>02</StepNum>
              <StepTitle>Deterministic scoring</StepTitle>
              <StepBody>
                We score each restaurant from 0-100 based on recency, severity, and frequency of violations. No
                opinions. No editorial.
              </StepBody>
            </StepCard>
            <StepCard>
              <StepNum>03</StepNum>
              <StepTitle>Plain English ratings</StepTitle>
              <StepBody>
                Sterling assigns one of five tiers - Exceptional, Strong, Fair, Concerning, Poor - and writes a single
                sentence summary you can read at a glance.
              </StepBody>
            </StepCard>
          </StepGrid>
          <HRule />
        </Container>
      </SectionWrap>

      {/* Screenshots */}
      <SterlingCoverflow
        eyebrow="Inside Sterling"
        title="Confidence, at a glance."
        subtitle="Scores built on real records, every visit tracked, and filters that fit your situation."
        images={[
          { src: '/screens/sterling/01.jpg', alt: 'The map - every kitchen in the city, rated' },
          { src: '/screens/sterling/02.jpg', alt: 'The score - trust in one number, nothing hidden' },
          { src: '/screens/sterling/03.jpg', alt: 'No black box - every point in the score accounted for' },
          { src: '/screens/sterling/04.jpg', alt: 'The Sterling Standard - the rarest mark of trust' },
          { src: '/screens/sterling/05.jpg', alt: 'Search and filter by the violations that matter to you' },
          { src: '/screens/sterling/06.jpg', alt: 'Discover - the cleanest places near you, ranked' },
          { src: '/screens/sterling/07.jpg', alt: 'Inspections - the full record, every visit' },
        ]}
        theme={{
          bg: 'linear-gradient(180deg, #F8F4EE 0%, #F1E9DD 100%)',
          glow: 'radial-gradient(ellipse 55% 45% at 50% 6%, rgba(199,93,61,0.08), transparent 70%)',
          borderTop: border,
          borderBottom: border,
          ink,
          muted,
          accent: terracotta,
          border,
          titleFont: SerifFamily,
          cardBg: '#F8F4EE',
        }}
      />

      {/* Sterling Standard */}
      <SectionWrap>
        <Container>
          <StandardWrap>
            <Eyebrow>The gold mark</Eyebrow>
            <NameRotator
              ref={nameRef}
              onMouseEnter={() => setNameHeld(true)}
              onMouseLeave={() => setNameHeld(false)}
              onFocus={() => setNameHeld(true)}
              onBlur={() => setNameHeld(false)}
            >
              <StandardMarquee>
                <Laurel size={72} />
                <StandardName key={STERLING_NAMES[nameIdx]}>
                  {STERLING_NAMES[nameIdx]}
                </StandardName>
                <Laurel flip size={72} />
              </StandardMarquee>
              {!reduceMotion && (
                <RotationToggle
                  type="button"
                  onClick={() => setNamePaused((p) => !p)}
                  aria-label={
                    namePaused
                      ? 'Resume the rotating Sterling Standard restaurant names'
                      : 'Pause the rotating Sterling Standard restaurant names'
                  }
                >
                  {namePaused ? 'Resume' : 'Pause'}
                </RotationToggle>
              )}
            </NameRotator>
            <StandardItalic>The Sterling Standard.</StandardItalic>
            <Body>
              Earned by restaurants with a score of 95 or above, three or more recent inspections, and zero critical
              violations in the last 12 months. About 16% of restaurants qualify.
            </Body>
            <StatStrip>
              <StatCell>
                <StatNumber>178,000+</StatNumber>
                <StatLabel>Restaurants tracked</StatLabel>
              </StatCell>
              <StatCell>
                <StatNumber>28,937</StatNumber>
                <StatLabel>Sterling Standard</StatLabel>
              </StatCell>
              <StatCell>
                <StatNumber>18</StatNumber>
                <StatLabel>Cities & states</StatLabel>
              </StatCell>
              <StatCell>
                <StatNumber>Weekly</StatNumber>
                <StatLabel>Refresh cadence</StatLabel>
              </StatCell>
            </StatStrip>
          </StandardWrap>
        </Container>
      </SectionWrap>

      {/* Cities */}
      <SectionWrap>
        <Container>
          <SerifTitle>Where Sterling works.</SerifTitle>
          <Body style={{ textAlign: 'center' }}>18 jurisdictions live, more shipping every month.</Body>
          <CitiesGrid>
            {cities.map((c) => (
              <CityCard key={c.name}>
                <div>
                  <CityName>{c.name}</CityName>
                  <CityCount>
                    {c.count} restaurants
                    {c.hot && <span className="visually-hidden"> · top coverage</span>}
                  </CityCount>
                </div>
                <CityDot $hot={c.hot} aria-hidden="true" />
              </CityCard>
            ))}
          </CitiesGrid>
          <CitiesFooter>
            More cities shipping monthly.
            <a
              href="mailto:support@sterlingdine.com?subject=Sterling%20%E2%80%94%20city%20request"
              rel="noopener noreferrer"
            >
              Request a city →
            </a>
          </CitiesFooter>
        </Container>
      </SectionWrap>

      {/* Everything included */}
      <SectionWrap>
        <Container>
          <SerifTitle>Everything included.</SerifTitle>
          <Body style={{ textAlign: 'center' }}>
            Every rating, plus more ways to stay on top of the places you care about.
          </Body>
          <PricingGrid>
            <PriceCard>
              <PlanName>The full register</PlanName>
              <PlanList>
                <PlanItem>
                  <Check />
                  All 178,000+ restaurants
                </PlanItem>
                <PlanItem>
                  <Check />
                  Plain-English ratings
                </PlanItem>
                <PlanItem>
                  <Check />
                  Sterling Standard mark
                </PlanItem>
                <PlanItem>
                  <Check />
                  Weekly updates
                </PlanItem>
              </PlanList>
              <OutlineButton href="#get-sterling">
                Get Sterling →
              </OutlineButton>
            </PriceCard>

            <PriceCard $featured>
              <PlanName>Stay in the know</PlanName>
              <PlanList>
                <PlanItem>
                  <Check />
                  Watch a restaurant - alerts on new inspections
                </PlanItem>
                <PlanItem>
                  <Check />
                  Travel mode - auto-switch when you visit other cities
                </PlanItem>
                <PlanItem>
                  <Check />
                  Health filters - hide pests, allergens, sanitation issues
                </PlanItem>
                <PlanItem>
                  <Check />
                  Priority support
                </PlanItem>
              </PlanList>
              <PrimaryButton href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                Download on the App Store
              </PrimaryButton>
              {PLAY_LIVE ? (
                <OutlineButton
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginTop: '0.6rem' }}
                >
                  Get it on Google Play
                </OutlineButton>
              ) : (
                <PlanPendingNote>Google Play - coming soon</PlanPendingNote>
              )}
            </PriceCard>
          </PricingGrid>
        </Container>
      </SectionWrap>

      {/* FAQ */}
      <SectionWrap>
        <Container>
          <SerifTitle>Questions.</SerifTitle>
          <FAQList>
            {faqs.map((f, i) => {
              const open = openFaq === i;
              const qId = `${faqNs}-q${i}`;
              const aId = `${faqNs}-a${i}`;
              return (
                <FAQItem key={f.q}>
                  <FAQQuestion
                    type="button"
                    id={qId}
                    $open={open}
                    aria-expanded={open}
                    aria-controls={aId}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    {f.q}
                    <span className="caret" aria-hidden="true">
                      ›
                    </span>
                  </FAQQuestion>
                  <FAQAnswer id={aId} role="region" aria-labelledby={qId} $open={open}>{f.a}</FAQAnswer>
                </FAQItem>
              );
            })}
          </FAQList>
        </Container>
      </SectionWrap>

      {/* Final CTA */}
      <FinalCTA id="get-sterling">
        <Container>
          <SerifTitle>Eat with confidence.</SerifTitle>
          <Body style={{ textAlign: 'center' }}>Every score traceable. Every record public.</Body>
          <div style={{ textAlign: 'center', marginTop: '2.25rem' }}>
            <CTARow>
              <PrimaryCTA href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <AppleLogoSVG />
                Download on the App Store
              </PrimaryCTA>
              {PLAY_LIVE ? (
                <StoreOutlineCTA href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <GooglePlayLogoSVG />
                  Get it on Google Play
                </StoreOutlineCTA>
              ) : (
                <StorePendingCTA>
                  <GooglePlayLogoSVG />
                  Google Play - coming soon
                </StorePendingCTA>
              )}
            </CTARow>
            <FinalCTASmallText>iPhone · iOS 17+ · Android 7.0+</FinalCTASmallText>
            <LegalLinksRow>
              <a href="/legal/sterling-privacy.html">Privacy Policy</a>
              <span>·</span>
              <a href="/legal/sterling-terms.html">Terms of Use</a>
            </LegalLinksRow>
          </div>
        </Container>
      </FinalCTA>
    </PageContainer>
  );
};

export default Sterling;
